import { useMemo, useRef, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient, getQueryFn } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { ContactPaymentTab } from "@/components/contact-payment-tab";
import { ContactRulesTab } from "@/components/contact-rules-tab";
import {
  Plus, Search, Download, Upload, Pencil, Trash2, Mail, Phone, Globe,
  Link2, FileText, X, ArrowUpDown, ArrowUp, ArrowDown, BookUser, Loader2, Users,
  Clock, SendHorizontal,
} from "lucide-react";

interface LabeledEmail { label: string; email: string }
interface LabeledPhone { label: string; phone: string }
interface LabeledUrl { label: string; url: string }

interface ContactCategory {
  id: string;
  name: string;
  isDefault: boolean;
}

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  businessName: string | null;
  categoryId: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
  emails: LabeledEmail[];
  phones: LabeledPhone[];
  websites: LabeledUrl[];
  additionalUrls: LabeledUrl[];
  notes: string | null;
  source: string;
  createdAt: string;
  updatedAt: string;
}

interface ContactDocument {
  id: string;
  title: string;
  docType: string;
  storageKind: string;
  mediaId: string | null;
  driveFileId: string | null;
  url: string | null;
  mediaFile: { id: string; name: string; fileUrl: string; fileType: string } | null;
  driveFile: { id: string; name: string; webViewLink: string | null } | null;
}
interface ContactShare { id: string; sharedWithId: string; permission: "view" | "edit"; fieldScope: string[] }

interface MediaFile {
  id: string;
  name: string;
  fileType: string;
}

interface DriveFileOption {
  id: string;
  name: string;
  mimeType: string;
  webViewLink: string | null;
}

type SortField = "name" | "businessName" | "category" | "createdAt";
type SortDir = "asc" | "desc";

const emptyForm = {
  firstName: "",
  lastName: "",
  businessName: "",
  categoryId: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  postalCode: "",
  country: "",
  emails: [] as LabeledEmail[],
  phones: [] as LabeledPhone[],
  websites: [] as LabeledUrl[],
  additionalUrls: [] as LabeledUrl[],
  notes: "",
};

type ContactForm = typeof emptyForm;

function contactDisplayName(c: Contact): string {
  const name = [c.firstName, c.lastName].filter(Boolean).join(" ").trim();
  return name || c.businessName || "Unnamed";
}

export default function ContactsPage() {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [pageTab, setPageTab] = useState<"contacts" | "invited">("contacts");

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [groupBy, setGroupBy] = useState<"none" | "category" | "letter" | "role">("none");
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [contactTab, setContactTab] = useState("details");
  const [form, setForm] = useState<ContactForm>(emptyForm);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [showNewCategory, setShowNewCategory] = useState(false);

  const [docTitle, setDocTitle] = useState("");
  const [docUrl, setDocUrl] = useState("");
  const [docType, setDocType] = useState<string>("website");
  const [docMediaId, setDocMediaId] = useState("");
  const [docDriveFileId, setDocDriveFileId] = useState("");
  const [docSource, setDocSource] = useState<"url" | "media" | "drive">("url");

  const [deleteTarget, setDeleteTarget] = useState<Contact | null>(null);
  const [importing, setImporting] = useState(false);
  const [shareRecipient, setShareRecipient] = useState("");
  const [sharePermission, setSharePermission] = useState<"view" | "edit">("view");
  const [shareScope, setShareScope] = useState<string[]>(["identity", "contact"]);

  // ── Friend invitations ──────────────────────────────────────────────────
  interface FriendInvitation {
    id: string;
    inviteeEmail: string;
    displayName: string | null;
    alias: string | null;
    status: string;
    expiresAt: string;
    createdAt: string;
  }

  const { data: sentInvitations = [], isLoading: invitationsLoading } = useQuery<FriendInvitation[]>({
    queryKey: ["/api/friend-invitations/sent"],
  });

  const pendingInvitations = sentInvitations.filter(inv => inv.status === "pending");

  const resendInvitationMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/friend-invitations/${id}/resend`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/friend-invitations/sent"] });
      toast({ title: "Invitation resent", description: "A fresh invitation link has been emailed." });
    },
    onError: () => toast({ title: "Failed to resend invitation", variant: "destructive" }),
  });

  const cancelInvitationMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/friend-invitations/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/friend-invitations/sent"] });
      toast({ title: "Invitation cancelled" });
    },
    onError: () => toast({ title: "Failed to cancel invitation", variant: "destructive" }),
  });
  // ────────────────────────────────────────────────────────────────────────

  const { data: contacts = [], isLoading } = useQuery<Contact[]>({
    queryKey: ["/api/contacts"],
  });

  // Fetched to enable "By role" grouping — cross-reference contact emails against assistants
  interface AssistantForRole {
    assistantType: string;
    user: { email: string };
  }
  const { data: allAssistantsForRole = [] } = useQuery<AssistantForRole[]>({
    queryKey: ["/api/assistants"],
  });

  // email (lowercase) → human-readable role label, precedence: Coach > VA > Assistant
  const roleByEmail = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of allAssistantsForRole) {
      const email = a.user?.email?.toLowerCase();
      if (!email) continue;
      const label = a.assistantType === "coach" ? "Coach"
        : a.assistantType === "virtual_assistant" ? "Virtual Assistant"
        : "Assistant";
      const existing = m.get(email);
      // Coach wins over any other role
      if (!existing || label === "Coach") m.set(email, label);
    }
    return m;
  }, [allAssistantsForRole]);

  const { data: categories = [] } = useQuery<ContactCategory[]>({
    queryKey: ["/api/contact-categories"],
  });

  const { data: documents = [] } = useQuery<ContactDocument[]>({
    queryKey: ["/api/contacts", editingContact?.id, "documents"],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!editingContact?.id && dialogOpen,
  });
  const { data: shares = [] } = useQuery<ContactShare[]>({
    queryKey: ["/api/contacts", editingContact?.id, "shares"],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!editingContact?.id && dialogOpen && contactTab === "sharing",
  });

  const { data: mediaFiles = [] } = useQuery<MediaFile[]>({
    queryKey: ["/api/media"],
    queryFn: getQueryFn({ on401: "returnNull" }) as any,
    enabled: dialogOpen,
  });

  const { data: driveFileOptions = [] } = useQuery<DriveFileOption[]>({
    queryKey: ["/api/drive-files"],
    queryFn: getQueryFn({ on401: "returnNull" }) as any,
    enabled: dialogOpen,
  });

  const categoryName = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of categories) m.set(c.id, c.name);
    return m;
  }, [categories]);

  const filtered = useMemo(() => {
    let list = contacts;
    if (categoryFilter !== "all") {
      if (categoryFilter === "uncategorized") list = list.filter(c => !c.categoryId);
      else list = list.filter(c => c.categoryId === categoryFilter);
    }
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(c =>
        contactDisplayName(c).toLowerCase().includes(q) ||
        (c.businessName || "").toLowerCase().includes(q) ||
        (c.emails || []).some(e => e.email.toLowerCase().includes(q)) ||
        (c.phones || []).some(p => p.phone.toLowerCase().includes(q)) ||
        (c.city || "").toLowerCase().includes(q) ||
        (categoryName.get(c.categoryId || "") || "").toLowerCase().includes(q)
      );
    }
    const dir = sortDir === "asc" ? 1 : -1;
    return [...list].sort((a, b) => {
      let av = "", bv = "";
      if (sortField === "name") { av = contactDisplayName(a); bv = contactDisplayName(b); }
      else if (sortField === "businessName") { av = a.businessName || ""; bv = b.businessName || ""; }
      else if (sortField === "category") { av = categoryName.get(a.categoryId || "") || ""; bv = categoryName.get(b.categoryId || "") || ""; }
      else { av = a.createdAt; bv = b.createdAt; }
      return av.localeCompare(bv) * dir;
    });
  }, [contacts, categoryFilter, search, sortField, sortDir, categoryName]);

  const groups = useMemo(() => {
    if (groupBy === "none") return [{ key: "", label: "", items: filtered }];
    const map = new Map<string, Contact[]>();
    const roleOrder = ["Coach", "Virtual Assistant", "Assistant", "Contact"];
    for (const c of filtered) {
      let key: string;
      if (groupBy === "category") {
        key = categoryName.get(c.categoryId || "") || "Uncategorized";
      } else if (groupBy === "role") {
        const matched = (c.emails || [])
          .map(e => roleByEmail.get(e.email.toLowerCase()))
          .filter(Boolean) as string[];
        // Pick the highest-precedence role among all contact emails
        key = roleOrder.find(r => matched.includes(r)) ?? "Contact";
      } else {
        key = (contactDisplayName(c)[0] || "#").toUpperCase();
      }
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(c);
    }
    if (groupBy === "role") {
      return roleOrder
        .filter(r => map.has(r))
        .map(r => ({ key: r, label: r, items: map.get(r)! }));
    }
    return Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([key, items]) => ({ key, label: key, items }));
  }, [filtered, groupBy, categoryName, roleByEmail]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["/api/contacts"] });

  const saveMutation = useMutation({
    mutationFn: async (data: ContactForm) => {
      const payload = {
        ...data,
        businessName: data.businessName || null,
        categoryId: data.categoryId || null,
        addressLine1: data.addressLine1 || null,
        addressLine2: data.addressLine2 || null,
        city: data.city || null,
        state: data.state || null,
        postalCode: data.postalCode || null,
        country: data.country || null,
        notes: data.notes || null,
        emails: data.emails.filter(e => e.email.trim()),
        phones: data.phones.filter(p => p.phone.trim()),
        websites: data.websites.filter(w => w.url.trim()),
        additionalUrls: data.additionalUrls.filter(u => u.url.trim()),
      };
      if (editingContact) {
        return apiRequest("PATCH", `/api/contacts/${editingContact.id}`, payload);
      }
      return apiRequest("POST", "/api/contacts", payload);
    },
    onSuccess: async (res) => {
      invalidate();
      if (!editingContact) {
        const created = await res.json();
        setEditingContact(created);
        toast({ title: "Contact created", description: "You can now add documents and media below." });
      } else {
        toast({ title: "Contact saved" });
      }
    },
    onError: () => toast({ title: "Failed to save contact", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/contacts/${id}`),
    onSuccess: () => {
      invalidate();
      setDeleteTarget(null);
      toast({ title: "Contact deleted" });
    },
    onError: () => toast({ title: "Failed to delete contact", variant: "destructive" }),
  });

  const createCategoryMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await apiRequest("POST", "/api/contact-categories", { name });
      return res.json();
    },
    onSuccess: (cat: ContactCategory) => {
      queryClient.invalidateQueries({ queryKey: ["/api/contact-categories"] });
      setForm(f => ({ ...f, categoryId: cat.id }));
      setNewCategoryName("");
      setShowNewCategory(false);
      toast({ title: "Category added" });
    },
    onError: () => toast({ title: "Failed to add category", variant: "destructive" }),
  });

  const addDocumentMutation = useMutation({
    mutationFn: async () => {
      if (!editingContact) throw new Error("No contact");
      const payload = docSource === "media"
        ? { title: docTitle, docType: "file", storageKind: "media", mediaId: docMediaId }
        : docSource === "drive"
        ? { title: docTitle, docType: "file", storageKind: "drive", driveFileId: docDriveFileId }
        : { title: docTitle, docType, storageKind: "url", url: docUrl };
      return apiRequest("POST", `/api/contacts/${editingContact.id}/documents`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/contacts", editingContact?.id, "documents"] });
      setDocTitle(""); setDocUrl(""); setDocMediaId(""); setDocDriveFileId("");
      toast({ title: "Document added" });
    },
    onError: () => toast({ title: "Failed to add document", variant: "destructive" }),
  });

  const deleteDocumentMutation = useMutation({
    mutationFn: (docId: string) =>
      apiRequest("DELETE", `/api/contacts/${editingContact!.id}/documents/${docId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/contacts", editingContact?.id, "documents"] });
    },
    onError: () => toast({ title: "Failed to remove document", variant: "destructive" }),
  });
  const shareMutation = useMutation({
    mutationFn: () => apiRequest("PUT", `/api/contacts/${editingContact!.id}/shares`, {
      recipients: [{ userId: shareRecipient.trim(), permission: sharePermission, fieldScope: shareScope }],
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/contacts", editingContact?.id, "shares"] });
      setShareRecipient("");
      toast({ title: "Contact shared" });
    },
    onError: () => toast({ title: "Unable to share contact", description: "The recipient must be an active assistant assigned to you.", variant: "destructive" }),
  });
  const revokeShareMutation = useMutation({
    mutationFn: (userId: string) => apiRequest("DELETE", `/api/contacts/${editingContact!.id}/shares/${userId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/contacts", editingContact?.id, "shares"] }),
    onError: () => toast({ title: "Unable to revoke share", variant: "destructive" }),
  });

  const resetDocForm = () => {
    setDocTitle(""); setDocUrl(""); setDocMediaId(""); setDocDriveFileId("");
    setDocSource("url"); setDocType("website");
    setShowNewCategory(false); setNewCategoryName("");
  };

  const openCreate = () => {
    setEditingContact(null);
    setForm(emptyForm);
    resetDocForm();
    setContactTab("details");
    setDialogOpen(true);
  };

  const openEdit = (c: Contact) => {
    setEditingContact(c);
    setForm({
      firstName: c.firstName || "",
      lastName: c.lastName || "",
      businessName: c.businessName || "",
      categoryId: c.categoryId || "",
      addressLine1: c.addressLine1 || "",
      addressLine2: c.addressLine2 || "",
      city: c.city || "",
      state: c.state || "",
      postalCode: c.postalCode || "",
      country: c.country || "",
      emails: c.emails || [],
      phones: c.phones || [],
      websites: c.websites || [],
      additionalUrls: c.additionalUrls || [],
      notes: c.notes || "",
    });
    resetDocForm();
    setContactTab("details");
    setDialogOpen(true);
  };

  const handleExport = async (format: "csv" | "vcf") => {
    try {
      const res = await fetch(`/api/contacts/export?format=${format}`, { credentials: "include" });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = format === "vcf" ? "contacts.vcf" : "contacts.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: "Export failed", variant: "destructive" });
    }
  };

  const handleImportFile = async (file: File) => {
    setImporting(true);
    try {
      const text = await file.text();
      const isVcf = file.name.toLowerCase().endsWith(".vcf") || text.trimStart().toUpperCase().startsWith("BEGIN:VCARD");
      const res = await apiRequest("POST", "/api/contacts/import", {
        format: isVcf ? "vcf" : "csv",
        data: text,
        source: "import-csv",
      });
      const result = await res.json();
      invalidate();
      toast({
        title: `Imported ${result.imported} contact${result.imported === 1 ? "" : "s"}`,
        description: result.skippedDuplicates > 0 ? `${result.skippedDuplicates} duplicate${result.skippedDuplicates === 1 ? "" : "s"} skipped.` : undefined,
      });
    } catch {
      toast({ title: "Import failed", description: "Make sure the file is a vCard (.vcf) or CSV export from Google, Outlook, or Apple Contacts.", variant: "destructive" });
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortDir(d => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDir("asc"); }
  };

  const SortIcon = ({ field }: { field: SortField }) =>
    sortField !== field ? <ArrowUpDown className="w-3.5 h-3.5 ml-1 inline opacity-50" />
      : sortDir === "asc" ? <ArrowUp className="w-3.5 h-3.5 ml-1 inline" />
      : <ArrowDown className="w-3.5 h-3.5 ml-1 inline" />;

  const setListItem = <K extends "emails" | "phones" | "websites" | "additionalUrls">(
    key: K, idx: number, patch: Partial<ContactForm[K][number]>,
  ) => {
    setForm(f => ({
      ...f,
      [key]: f[key].map((item, i) => (i === idx ? { ...item, ...patch } : item)),
    }));
  };

  const removeListItem = (key: "emails" | "phones" | "websites" | "additionalUrls", idx: number) => {
    setForm(f => ({ ...f, [key]: f[key].filter((_, i) => i !== idx) }));
  };

  const renderContactRow = (c: Contact) => (
    <TableRow key={c.id} className="cursor-pointer" onClick={() => openEdit(c)} data-testid={`row-contact-${c.id}`}>
      <TableCell className="font-medium">{contactDisplayName(c)}</TableCell>
      <TableCell className="hidden md:table-cell">{c.businessName || "—"}</TableCell>
      <TableCell className="hidden sm:table-cell">
        {c.categoryId && categoryName.get(c.categoryId)
          ? <Badge variant="secondary">{categoryName.get(c.categoryId)}</Badge>
          : <span className="text-muted-foreground">—</span>}
      </TableCell>
      <TableCell className="hidden lg:table-cell">
        {c.emails?.[0]?.email
          ? <span className="flex items-center gap-1 text-sm"><Mail className="w-3.5 h-3.5" />{c.emails[0].email}{c.emails.length > 1 && <Badge variant="outline" className="ml-1 text-xs">+{c.emails.length - 1}</Badge>}</span>
          : <span className="text-muted-foreground">—</span>}
      </TableCell>
      <TableCell className="hidden lg:table-cell">
        {c.phones?.[0]?.phone
          ? <span className="flex items-center gap-1 text-sm"><Phone className="w-3.5 h-3.5" />{c.phones[0].phone}</span>
          : <span className="text-muted-foreground">—</span>}
      </TableCell>
      <TableCell className="text-right" onClick={e => e.stopPropagation()}>
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`button-edit-contact-${c.id}`}>
            <Pencil className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(c)} data-testid={`button-delete-contact-${c.id}`}>
            <Trash2 className="w-4 h-4 text-destructive" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );

  return (
    <div className="w-full p-4 md:p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <BookUser className="w-6 h-6" />
          <h1 className="text-2xl font-bold" data-testid="text-contacts-title">Contacts</h1>
          <Badge variant="outline">{contacts.length}</Badge>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".vcf,.csv,text/vcard,text/csv"
            className="hidden"
            onChange={e => { const f = e.target.files?.[0]; if (f) handleImportFile(f); }}
            data-testid="input-import-file"
          />
          <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={importing} data-testid="button-import-contacts">
            {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Import
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="button-export-contacts">
                <Download className="w-4 h-4 mr-2" />Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport("csv")} data-testid="button-export-csv">CSV (Google / Outlook)</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("vcf")} data-testid="button-export-vcf">vCard (Apple / universal)</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={openCreate} data-testid="button-add-contact">
            <Plus className="w-4 h-4 mr-2" />Add Contact
          </Button>
        </div>
      </div>

      <Tabs value={pageTab} onValueChange={v => setPageTab(v as "contacts" | "invited")} className="w-full">
        <TabsList>
          <TabsTrigger value="contacts" data-testid="tab-page-contacts">
            <Users className="w-4 h-4 mr-2" />
            Contacts ({contacts.length})
          </TabsTrigger>
          <TabsTrigger value="invited" data-testid="tab-page-invited">
            <SendHorizontal className="w-4 h-4 mr-2" />
            Invited ({pendingInvitations.length})
          </TabsTrigger>
        </TabsList>

        {/* ── Contacts tab ── */}
        <TabsContent value="contacts" className="space-y-4 mt-4">
          <p className="text-sm text-muted-foreground">
            Import accepts vCard (.vcf) and CSV files exported from Google Contacts, Outlook, or Apple Contacts.
          </p>

          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search contacts..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9"
                data-testid="input-search-contacts"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[180px]" data-testid="select-filter-category">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                <SelectItem value="uncategorized">Uncategorized</SelectItem>
                {categories.filter(c => c.name).map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={groupBy} onValueChange={v => setGroupBy(v as any)}>
              <SelectTrigger className="w-[170px]" data-testid="select-group-by">
                <SelectValue placeholder="Group by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No grouping</SelectItem>
                <SelectItem value="category">By category</SelectItem>
                <SelectItem value="letter">By letter</SelectItem>
                <SelectItem value="role">By role</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                {contacts.length === 0
                  ? "No contacts yet. Add your first contact or import from Google, Outlook, or Apple."
                  : "No contacts match your search."}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {groups.map(group => (
                <div key={group.key || "__all"}>
                  {group.label && (
                    <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                      {group.label} <span className="font-normal">({group.items.length})</span>
                    </h2>
                  )}
                  <Card>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("name")}>Name<SortIcon field="name" /></TableHead>
                            <TableHead className="hidden md:table-cell cursor-pointer select-none" onClick={() => toggleSort("businessName")}>Business<SortIcon field="businessName" /></TableHead>
                            <TableHead className="hidden sm:table-cell cursor-pointer select-none" onClick={() => toggleSort("category")}>Category<SortIcon field="category" /></TableHead>
                            <TableHead className="hidden lg:table-cell">Email</TableHead>
                            <TableHead className="hidden lg:table-cell">Phone</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {group.items.map(renderContactRow)}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ── Invited tab ── */}
        <TabsContent value="invited" className="mt-4">
          <Card>
            <CardContent className="pt-6">
              {invitationsLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
              ) : pendingInvitations.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground" data-testid="empty-contact-invitations">
                  <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="font-medium">No pending invitations</p>
                  <p className="text-sm mt-1">People you invite to connect will appear here until they accept.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingInvitations.map(inv => (
                    <div
                      key={inv.id}
                      className="flex items-start md:items-center justify-between gap-4 p-4 border rounded-lg flex-col md:flex-row"
                      data-testid={`contact-invitation-card-${inv.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium" data-testid={`contact-invitation-email-${inv.id}`}>
                          {inv.inviteeEmail}
                        </p>
                        {inv.displayName && (
                          <p className="text-sm text-muted-foreground">{inv.displayName}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          Expires: {new Date(inv.expiresAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => resendInvitationMutation.mutate(inv.id)}
                          disabled={resendInvitationMutation.isPending}
                          data-testid={`button-resend-contact-invitation-${inv.id}`}
                        >
                          <Mail className="w-4 h-4 mr-1" />
                          Resend
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              data-testid={`button-cancel-contact-invitation-${inv.id}`}
                            >
                              <X className="w-4 h-4 mr-1" />
                              Cancel
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Cancel invitation</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to cancel the invitation to{" "}
                                <strong>{inv.inviteeEmail}</strong>? The invitation link will stop working.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Keep Invitation</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => cancelInvitationMutation.mutate(inv.id)}
                                className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                              >
                                Cancel Invitation
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingContact ? `Edit ${contactDisplayName(editingContact)}` : "Add Contact"}</DialogTitle>
          </DialogHeader>

          {editingContact && (
            <div className="max-w-3xl mx-auto w-full mb-4">
              <Tabs value={contactTab} onValueChange={setContactTab}>
                <TabsList>
                  <TabsTrigger value="details" data-testid="tab-contact-details">Details</TabsTrigger>
                  <TabsTrigger value="payment" data-testid="tab-contact-payment">Payment</TabsTrigger>
                  <TabsTrigger value="rules" data-testid="tab-contact-rules">Rules</TabsTrigger>
                  <TabsTrigger value="sharing" data-testid="tab-contact-sharing"><Users className="w-3.5 h-3.5 mr-1" />Sharing</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          )}

          {editingContact && contactTab === "payment" ? (
            <div className="max-w-3xl mx-auto w-full pb-8">
              <ContactPaymentTab contactId={editingContact.id} />
            </div>
          ) : editingContact && contactTab === "rules" ? (
            <div className="max-w-3xl mx-auto w-full pb-8">
              <ContactRulesTab contactId={editingContact.id} />
            </div>
          ) : editingContact && contactTab === "sharing" ? (
            <div className="max-w-3xl mx-auto w-full space-y-4 pb-8">
              <p className="text-sm text-muted-foreground">Share only the fields an active assistant needs. Access ends immediately when a share or assistant relationship is revoked.</p>
              <div className="border rounded-md p-3 space-y-3">
                <Label htmlFor="share-recipient">Assistant user ID</Label>
                <Input id="share-recipient" value={shareRecipient} onChange={e => setShareRecipient(e.target.value)} placeholder="Active assistant user ID" data-testid="input-share-recipient" />
                <div className="flex flex-wrap items-center gap-3">
                  <Select value={sharePermission} onValueChange={v => setSharePermission(v as "view" | "edit")}>
                    <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="view">View</SelectItem><SelectItem value="edit">Edit</SelectItem></SelectContent>
                  </Select>
                  {["identity", "contact", "address", "notes", "documents"].map(scope => (
                    <label key={scope} className="flex items-center gap-1 text-sm capitalize">
                      <input type="checkbox" checked={shareScope.includes(scope)} onChange={() => setShareScope(s => s.includes(scope) ? s.filter(x => x !== scope) : [...s, scope])} />{scope}
                    </label>
                  ))}
                </div>
                <Button size="sm" disabled={!shareRecipient.trim() || !shareScope.length || shareMutation.isPending} onClick={() => shareMutation.mutate()}>Share contact</Button>
              </div>
              {shares.map(share => <div key={share.id} className="flex items-center justify-between border rounded-md px-3 py-2 text-sm">
                <span className="min-w-0 truncate">{share.sharedWithId} · {share.permission} · {share.fieldScope.join(", ")}</span>
                <Button variant="ghost" size="sm" onClick={() => revokeShareMutation.mutate(share.sharedWithId)}>Revoke</Button>
              </div>)}
              {!shares.length && <p className="text-sm text-muted-foreground">Not shared with anyone.</p>}
            </div>
          ) : (
          <div className="space-y-6 max-w-3xl mx-auto w-full pb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName">First name</Label>
                <Input id="firstName" value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} data-testid="input-first-name" />
              </div>
              <div>
                <Label htmlFor="lastName">Last name</Label>
                <Input id="lastName" value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} data-testid="input-last-name" />
              </div>
              <div>
                <Label htmlFor="businessName">Business name</Label>
                <Input id="businessName" value={form.businessName} onChange={e => setForm(f => ({ ...f, businessName: e.target.value }))} data-testid="input-business-name" />
              </div>
              <div>
                <Label>Category</Label>
                <div className="flex gap-2">
                  <Select value={form.categoryId || "none"} onValueChange={v => setForm(f => ({ ...f, categoryId: v === "none" ? "" : v }))}>
                    <SelectTrigger className="flex-1" data-testid="select-contact-category">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No category</SelectItem>
                      {categories.filter(c => c.name).map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button type="button" variant="outline" onClick={() => setShowNewCategory(s => !s)} data-testid="button-toggle-new-category">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {showNewCategory && (
                  <div className="flex gap-2 mt-2">
                    <Input placeholder="New category name" value={newCategoryName} onChange={e => setNewCategoryName(e.target.value)} data-testid="input-new-category" />
                    <Button
                      type="button"
                      disabled={!newCategoryName.trim() || createCategoryMutation.isPending}
                      onClick={() => createCategoryMutation.mutate(newCategoryName.trim())}
                      data-testid="button-create-category"
                    >
                      Add
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Address</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input placeholder="Street address" value={form.addressLine1} onChange={e => setForm(f => ({ ...f, addressLine1: e.target.value }))} className="md:col-span-2" data-testid="input-address1" />
                <Input placeholder="Apt, suite, etc. (optional)" value={form.addressLine2} onChange={e => setForm(f => ({ ...f, addressLine2: e.target.value }))} className="md:col-span-2" data-testid="input-address2" />
                <Input placeholder="City" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} data-testid="input-city" />
                <Input placeholder="State / region" value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} data-testid="input-state" />
                <Input placeholder="Postal code" value={form.postalCode} onChange={e => setForm(f => ({ ...f, postalCode: e.target.value }))} data-testid="input-postal" />
                <Input placeholder="Country" value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))} data-testid="input-country" />
              </div>
            </div>

            {([
              { key: "emails" as const, title: "Emails", icon: Mail, valueKey: "email" as const, placeholder: "name@example.com", labelPlaceholder: "work / home" },
              { key: "phones" as const, title: "Phone numbers", icon: Phone, valueKey: "phone" as const, placeholder: "+1 555 123 4567", labelPlaceholder: "mobile / office" },
              { key: "websites" as const, title: "Websites", icon: Globe, valueKey: "url" as const, placeholder: "https://example.com", labelPlaceholder: "label" },
              { key: "additionalUrls" as const, title: "Additional URLs", icon: Link2, valueKey: "url" as const, placeholder: "https://...", labelPlaceholder: "label" },
            ]).map(section => (
              <div key={section.key}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold flex items-center gap-2"><section.icon className="w-4 h-4" />{section.title}</h3>
                  <Button
                    type="button" variant="outline" size="sm"
                    onClick={() => setForm(f => ({ ...f, [section.key]: [...f[section.key], { label: "", [section.valueKey]: "" } as any] }))}
                    data-testid={`button-add-${section.key}`}
                  >
                    <Plus className="w-4 h-4 mr-1" />Add
                  </Button>
                </div>
                {form[section.key].length === 0 && <p className="text-sm text-muted-foreground">None added.</p>}
                <div className="space-y-2">
                  {form[section.key].map((item: any, idx: number) => (
                    <div key={idx} className="flex gap-2">
                      <Input
                        className="w-32"
                        placeholder={section.labelPlaceholder}
                        value={item.label}
                        onChange={e => setListItem(section.key, idx, { label: e.target.value } as any)}
                      />
                      <Input
                        className="flex-1"
                        placeholder={section.placeholder}
                        value={item[section.valueKey]}
                        onChange={e => setListItem(section.key, idx, { [section.valueKey]: e.target.value } as any)}
                        data-testid={`input-${section.key}-${idx}`}
                      />
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeListItem(section.key, idx)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea id="notes" rows={3} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} data-testid="input-notes" />
            </div>

            {/* Documents & media — only for saved contacts */}
            <div>
              <h3 className="font-semibold flex items-center gap-2 mb-2"><FileText className="w-4 h-4" />Documents &amp; media</h3>
              {!editingContact ? (
                <p className="text-sm text-muted-foreground">Save the contact first to attach documents, media, and links.</p>
              ) : (
                <div className="space-y-3">
                  {documents.length > 0 && (
                    <div className="space-y-2">
                      {documents.map(doc => {
                        const href = doc.storageKind === "media"
                          ? (doc.mediaFile ? `/api/media/${doc.mediaFile.id}/content` : undefined)
                          : doc.storageKind === "drive"
                          ? (doc.driveFile?.webViewLink || undefined)
                          : (doc.url || undefined);
                        return (
                          <div key={doc.id} className="flex items-center justify-between gap-2 border rounded-md px-3 py-2">
                            <div className="min-w-0">
                              <p className="font-medium truncate">{doc.title}</p>
                              <p className="text-xs text-muted-foreground truncate">
                                {doc.storageKind === "media" ? `Media library: ${doc.mediaFile?.name || "file"}`
                                  : doc.storageKind === "drive" ? `Google Drive: ${doc.driveFile?.name || "file"}`
                                  : doc.url}
                              </p>
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                              {href && (
                                <Button asChild variant="ghost" size="icon">
                                  <a href={href} target="_blank" rel="noopener noreferrer"><Globe className="w-4 h-4" /></a>
                                </Button>
                              )}
                              <Button variant="ghost" size="icon" onClick={() => deleteDocumentMutation.mutate(doc.id)} data-testid={`button-delete-doc-${doc.id}`}>
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="border rounded-md p-3 space-y-2">
                    <div className="flex gap-2">
                      <Select value={docSource} onValueChange={v => setDocSource(v as any)}>
                        <SelectTrigger className="w-[160px]" data-testid="select-doc-source">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="url">Link / URL</SelectItem>
                          <SelectItem value="media">Media library</SelectItem>
                          <SelectItem value="drive">Google Drive</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input placeholder="Title" value={docTitle} onChange={e => setDocTitle(e.target.value)} className="flex-1" data-testid="input-doc-title" />
                    </div>
                    {docSource === "url" ? (
                      <div className="flex gap-2">
                        <Select value={docType} onValueChange={setDocType}>
                          <SelectTrigger className="w-[160px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="website">Website</SelectItem>
                            <SelectItem value="image-url">Image URL</SelectItem>
                            <SelectItem value="video-url">Video URL</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input placeholder="https://..." value={docUrl} onChange={e => setDocUrl(e.target.value)} className="flex-1" data-testid="input-doc-url" />
                      </div>
                    ) : docSource === "media" ? (
                      <Select value={docMediaId || "none"} onValueChange={v => setDocMediaId(v === "none" ? "" : v)}>
                        <SelectTrigger data-testid="select-doc-media">
                          <SelectValue placeholder="Pick a media file" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Select a file...</SelectItem>
                          {(mediaFiles || []).filter(m => m.name).map(m => (
                            <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <>
                        <Select value={docDriveFileId || "none"} onValueChange={v => setDocDriveFileId(v === "none" ? "" : v)}>
                          <SelectTrigger data-testid="select-doc-drive">
                            <SelectValue placeholder="Pick a Drive file" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Select a Drive file...</SelectItem>
                            {(driveFileOptions || []).filter(f => f.name).map(f => (
                              <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {(driveFileOptions || []).length === 0 && (
                          <p className="text-xs text-muted-foreground">No synced Drive files found. Connect Google Drive on the Drive page first.</p>
                        )}
                      </>
                    )}
                    <Button
                      type="button"
                      disabled={
                        addDocumentMutation.isPending ||
                        !docTitle.trim() ||
                        (docSource === "url" ? !docUrl.trim() : docSource === "media" ? !docMediaId : !docDriveFileId)
                      }
                      onClick={() => addDocumentMutation.mutate()}
                      data-testid="button-add-document"
                    >
                      <Plus className="w-4 h-4 mr-1" />Add document
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
          )}

          <DialogFooter className="max-w-3xl mx-auto w-full">
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="button-cancel-contact">Close</Button>
            <Button
              onClick={() => saveMutation.mutate(form)}
              disabled={saveMutation.isPending || (!form.firstName.trim() && !form.lastName.trim() && !form.businessName.trim())}
              data-testid="button-save-contact"
            >
              {saveMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingContact ? "Save changes" : "Create contact"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={open => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete contact</DialogTitle>
          </DialogHeader>
          <div className="max-w-md mx-auto w-full space-y-4">
            <p>
              Are you sure you want to delete{" "}
              <span className="font-semibold">{deleteTarget ? contactDisplayName(deleteTarget) : ""}</span>?
              This also removes any attached documents. This cannot be undone.
            </p>
          </div>
          <DialogFooter className="max-w-md mx-auto w-full">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
              data-testid="button-confirm-delete"
            >
              {deleteMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
